import { useEffect, useState } from "react";
import LiveCentreLng from "./LiveCentreLng";
import { MapContainer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { BASEMAPS, DIRECTORY_DEFAULT_BASEMAP, readBasemap, resolveBasemap, writeBasemap } from "../lib/basemap";
import { Basemaps } from "../pages/GroundsMap";
import MapFullScreen from "./kit/MapFullScreen";
import MapControlPill from "./kit/MapControlPill";
import MapFullScreenSync from "./kit/MapFullScreenSync";
import { MapFitter } from "./MapFitter";
import { NZ_FALLBACK_CENTER, NZ_FALLBACK_ZOOM, applyFit, computeCentredFit, computeMapFit, fitAnchorLng, fitSignature, placeLatLng } from "../lib/mapFit";

/**
 * Issue #757 review round 2, B3 — the map band keeps its SHIPPED behaviour,
 * as a fourth way in, rather than a new hard-wired satellite map: same
 * `BASEMAPS`/`nw_basemap` preference and the same #183 street/satellite
 * toggle `GroundsMap.jsx`'s own two maps already use (`Basemaps`, exported
 * from there for exactly this reuse — see that file's own comment at the
 * export site). #361's imagery decision stays a one-line url swap in
 * `lib/basemap.js`, unchanged by this file.
 *
 * PERMANENT, per `DirectionA1.dc.html`'s drawn band under the search
 * line (118px in the drawing; `clamp(220px, 40vh, 360px)` since D236/#1193,
 * which overrides the drawn height, plus a full-screen button) — visible on arrival, not only once a search returns rows with
 * coordinates. `rows` is whatever the caller currently has plotted (the
 * live search results, or a near-me ranking); an empty array still renders
 * the map, centred on `fallbackCenter`.
 *
 */
const groundMarkerIcon = new L.DivIcon({
  className: "log-ground-marker",
  html: `<div aria-hidden="true" style="width:22px;height:22px;border-radius:50%;background:#0B2E59;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// NZ_FALLBACK_CENTER / NZ_FALLBACK_ZOOM live in `lib/mapFit.js` since #1619 (the new-look directory map
// opens on the same view; one definition, and `GroundsMap.jsx` cannot import this file without a cycle).

/** Recentres/rezooms an already-mounted map when its target changes —
 * `MapContainer`'s own `center`/`zoom` props only apply on FIRST mount
 * (leaflet's own rule, the same one `GroundsMap.jsx`'s `MapFitter` exists
 * to work around); a plain prop change here would otherwise be silently
 * ignored on every later search. */
function MapBandFitter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
}

// Round 5 conformance (DirectionA1.dc.html) — the drawing's own
// `data-dc-id="log-map-band"`, not "log-ground-map-band" (round 2's
// guess). Renamed to match exactly; structure-check joins by literal
// string.
// #1573 — the one reason this band ever fits under: the first rows to arrive frame the map (MapFitter's
// `fitFirstArrival`), and nothing after that moves a map the player has panned.
const FIT_REASON = "frame-all-pins";
// #1700 N2: the preview's pin frame. A pin is 22px (radius 11); the chip is 44px tall at `bottom: 8`.
const PREVIEW_PAD_SIDE = 24;
const PREVIEW_PAD_TOP = 24;
const PREVIEW_PAD_BOTTOM = 8 + 44 + 11 + 4;

/**
 * `fit` (default false, #1573, D367.19) frames ALL the plotted pins instead of centring on the first one: the
 * dashboard's "Where I've played" map. One pin centres on it at this band's own zoom, no pins leaves the fallback
 * view. Off, the band behaves exactly as before.
 *
 * #1610: `centre` (`{ lat, lng }`, with `fit`) frames the pins around that point (`computeCentredFit`, the
 * "Use my location" frame of #1609) instead of their bounding box. `fitReason` is the question the frame answers:
 * #1622 (D439): `preview` (default false) is the Dashboard's "Where I've played" card: a 160px map nothing can move,
 * with no control pill and no full-screen button, hidden from assistive tech (the card's link names it).
 * `holdFirstArrival` waits for the caller's lists before the first frame (MapFitter, #1494). When `fitReason` changes the map re-frames (unless the player has moved it), so a search or near me after arrival moves it.
 */
/**
 * #1622 round 2 (F1, F2): the credit the preview's tiles must carry, drawn OUTSIDE the preview box. The box is
 * `inert` and sits inside the card's router link, so leaflet's own attribution there would be unreachable and its
 * links would open Grounds. The links here open the provider in a new tab. Same stored choice as the band reads;
 * the preview has no control to change it. The markup is the constant in `lib/basemap.js`, never user input.
 */
export function BasemapCredit({ style }) {
  const id = resolveBasemap(readBasemap(), DIRECTORY_DEFAULT_BASEMAP);
  const b = BASEMAPS.find((x) => x.id === id) || BASEMAPS[0];
  const html = b.attribution.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  return <span data-testid="basemap-credit" style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function LogGroundMapBand({
  rows,
  fallbackCenter = NZ_FALLBACK_CENTER,
  fallbackZoom = NZ_FALLBACK_ZOOM,
  onPick,
  testId = "log-map-band",
  fit = false,
  centre = undefined,
  fitReason = FIT_REASON,
  holdFirstArrival = false,
  preview = false,
}) {
  const [basemap, setBasemapState] = useState(readBasemap);
  const active = resolveBasemap(basemap, DIRECTORY_DEFAULT_BASEMAP);
  const chooseBasemap = (id) => {
    setBasemapState(id);
    writeBasemap(id);
  };

  const plotted = (rows || []).filter(
    (g) => Number.isFinite(g.latitude ?? g.lat) && Number.isFinite(g.longitude ?? g.lng),
  );
  const center = plotted.length > 0
    ? [plotted[0].latitude ?? plotted[0].lat, plotted[0].longitude ?? plotted[0].lng]
    : fallbackCenter;
  const zoom = plotted.length > 0 ? 12 : fallbackZoom;
  let pinFit = null;
  if (fit) {
    const pins = plotted.map((g) => ({ latitude: g.latitude ?? g.lat, longitude: g.longitude ?? g.lng }));
    pinFit = centre ? computeCentredFit(centre, pins, { wrap: true }) : computeMapFit(pins, { wrap: true });
    // A single pin (or several at one spot) keeps this band's own zoom, not the directory's town-scale one.
    if (pinFit.kind === "point") pinFit = { ...pinFit, zoom };
    // #1700 N2: the preview's "Tap to explore" chip covers the bottom-right corner (44px tall, 8px in), so the pins
    // are framed above it: PREVIEW_PAD_BOTTOM is the chip's strip plus a pin's radius. The sides keep a pin's width clear.
    if (preview && pinFit.kind === "bounds") pinFit = { ...pinFit, paddingTopLeft: [PREVIEW_PAD_SIDE, PREVIEW_PAD_TOP], paddingBottomRight: [PREVIEW_PAD_SIDE, PREVIEW_PAD_BOTTOM] };
  }

  // #1619 (D439): the map wraps, so a pin is drawn at the copy of its longitude nearest the LIVE view centre
  // (`LiveCentreLng`); the first frame uses the fit's centre.
  const [liveLng, setLiveLng] = useState(() => fitAnchorLng(pinFit, center[1]));

  const PREVIEW_OFF = { dragging: false, touchZoom: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, zoomControl: false };
  const mapBody = (closeThen) => (
    <MapContainer
      center={center}
      zoom={zoom}
      worldCopyJump
      style={{ height: "100%", width: "100%" }}
      scrollWheelZoom={false}
      zoomControl={false}
      {...(preview ? { ...PREVIEW_OFF, attributionControl: false } : {})}
    >
      <Basemaps active={active} onChoose={chooseBasemap} tapBox wrap pill />
      {/* #1620 (D439, D449): the one compact control on both looks, a deliberate D217 exception. #1622: not on the preview. */}
      {!preview && <MapControlPill basemap={active} onBasemap={chooseBasemap} />}
      <LiveCentreLng onChange={setLiveLng} />
      {fit ? (
        <MapFitter fit={pinFit} signature={fitSignature(pinFit)} reason={fitReason} fitFirstArrival holdFirstArrival={holdFirstArrival} reframeOnResize={preview} />
      ) : (
        <MapBandFitter center={center} zoom={zoom} />
      )}
      {/* Re-frame for the new box after the full-screen button opens or closes the map. */}
      {!preview && (
        <MapFullScreenSync
          onResized={(map) => (pinFit && pinFit.kind !== "none" ? applyFit(map, pinFit, { reducedMotion: true }) : map.setView(center, zoom, { animate: false }))}
        />
      )}
      {plotted.map((g) => (
        <Marker
          key={g.ground_id || g.groundId || g.name}
          position={placeLatLng([g.latitude ?? g.lat, g.longitude ?? g.lng], liveLng)}
          icon={groundMarkerIcon}
          // #1176: out of the tab order. Leaflet maps Enter to popups only,
          // so this pin could not pick the ground from the keyboard; the
          // search rows above offer the same grounds as buttons.
          keyboard={false}
          // #1622: a preview pin does nothing, so a tap on it reaches the card's link.
          interactive={!preview}
          // The same `onPick` the inline map uses; in full screen the
          // overlay closes first so the step change is not undone by Back.
          eventHandlers={preview ? undefined : { click: () => closeThen(() => onPick(g.name, g.ground_id ?? g.groundId)) }}
        />
      ))}
    </MapContainer>
  );

  // #1622 (D439): the Dashboard preview is a plain box, not the full-screen wrapper.
  if (preview) {
    return (
      <div data-testid={testId} aria-hidden="true" inert style={{ height: 160, width: "100%", position: "relative", overflow: "hidden", borderRadius: "var(--nw-radius-card)" }}>
        {mapBody(() => {})}
      </div>
    );
  }

  return (
    <MapFullScreen testId={testId} controlInPill>
      {({ closeThen }) => mapBody(closeThen)}
    </MapFullScreen>
  );
}
